import axios, { AxiosError } from 'axios';
import { auth } from '../firebase'; // your firebase.ts file

interface ApiError {
  message?: string;
  error?: string;
}

export const backendAPI = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});


interface Business {
  id: number;
  name: string;
  registryId: string;
  status: string;
  location: string;
  businessId: number;
  userId: number;
  createdAt: string;
  updatedAt: string;
  templateId?: number | null;
  logo?: string | null;
  details?: {
    description?: string;
    investment_level?: string;
    sector?: string;
    scalability?: string;
    economic_activity?: string;
    business_model?: string;
  };
}

type CampaignPayload = {
  type: string;
  data: Record<string, any>;
};


export const generate_campaign = async (business: Business, payload: CampaignPayload) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.post(
      'marketing/generate_campaign',
      {business, payload},
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    return response;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Generate marketing campaign failed'
    );
  }
};

export const propagate_campaign = async (campaignId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();

    const response = await backendAPI.post(
      'marketing/propagate_campaign',
      { campaignId },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Propagate campaign failed'
    );
  }
};


export const get_campaign = async (campaignId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.get('marketing/campaign', {
      params: {
        campaignId: campaignId
      },
      headers: {
        Authorization: `Bearer ${idToken}`,
      }
    });
    return response;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Get business checklist failed'
    );
  }
};

export const generate_newsletter = async (business: Business, history: { role: 'user' | 'assistant' | 'system'; content: string }[]) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      'marketing/generate_newsletter',
      {business, history},
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Generate newsletter failed'
    );
  }
};

export const edit_newsletter = async (business: Business) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.post(
      'marketing/generate_newsletter',
      {business},
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Generate newsletter failed'
    );
  }
};

export const get_mailchimp_templates = async (campaignId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();

    const response = await backendAPI.get('marketing/mailchimp_templates', {
      params: {
        campaignId: campaignId,
      },
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    return response;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Get Mailchimp templates failed'
    );
  }
};

export const regenerate_newsletter = async (campaignId: number, businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      'marketing/regenerate_newsletter',
      {campaignId, businessId},
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    return response;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Generate newsletter failed'
    );
  }
};

export const generate_demo_newsletter = async (business: Business, query: string) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      'marketing/generate_demo_newsletter',
      {business, query},
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Generate newsletter failed'
    );
  }
};

export const get_marketing_overview = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.get('marketing/overview', {
      params: { businessId },
      headers: { Authorization: `Bearer ${idToken}` },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Get marketing overview failed'
    );
  }
};

export const get_marketing_campaigns = async (
  businessId: number,
  options?: { status?: string; type?: string; limit?: number }
) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.get('marketing/campaigns', {
      params: {
        businessId,
        status: options?.status,
        type: options?.type,
        limit: options?.limit,
      },
      headers: { Authorization: `Bearer ${idToken}` },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Get marketing campaigns failed'
    );
  }
};

export const generate_linkedin_post = async (business: Business, prompt: string) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.post(
      'marketing/linkedin/generate_post',
      { business, prompt },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Generate LinkedIn post failed'
    );
  }
};

export const publish_linkedin_post = async (businessId: number, text: string, campaignId?: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.post(
      'tools/linkedin/publish',
      { businessId, text, campaignId },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error || 'Publish LinkedIn post failed'
    );
  }
};

export const schedule_campaign = async (campaignId: number, scheduledSendTime: string) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.post(
      'marketing/schedule_campaign',
      { campaignId, scheduledSendTime },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Schedule campaign failed'
    );
  }
};

async function authedPost(path: string, body: Record<string, unknown>) {
  const user = auth.currentUser;
  if (!user) throw new Error("User not logged in");
  const idToken = await user.getIdToken();
  return backendAPI.post(path, body, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
}

export const update_campaign = async (
  businessId: number,
  campaignId: number,
  updates: Record<string, unknown>
) => {
  try {
    const response = await authedPost('marketing/update_campaign', {
      businessId,
      campaignId,
      updates,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Update campaign failed'
    );
  }
};

export const cancel_scheduled_campaign = async (businessId: number, campaignId: number) => {
  try {
    const response = await authedPost('marketing/cancel_scheduled_campaign', {
      businessId,
      campaignId,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Cancel scheduled campaign failed'
    );
  }
};

export const duplicate_campaign = async (businessId: number, campaignId: number) => {
  try {
    const response = await authedPost('marketing/duplicate_campaign', {
      businessId,
      campaignId,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Duplicate campaign failed'
    );
  }
};

export const archive_campaign = async (businessId: number, campaignId: number) => {
  try {
    const response = await authedPost('marketing/archive_campaign', {
      businessId,
      campaignId,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Archive campaign failed'
    );
  }
};

export const delete_campaign = async (businessId: number, campaignId: number) => {
  try {
    const response = await authedPost('marketing/delete_campaign', {
      businessId,
      campaignId,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Delete campaign failed'
    );
  }
};

export const retry_campaign = async (businessId: number, campaignId: number) => {
  try {
    const response = await authedPost('marketing/retry_campaign', {
      businessId,
      campaignId,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      'Retry campaign failed'
    );
  }
};

export const schedule_linkedin_post = async (
  businessId: number,
  text: string,
  scheduledSendTime: string,
  title?: string
) => {
  try {
    const response = await authedPost('marketing/linkedin/schedule_post', {
      businessId,
      text,
      scheduledSendTime,
      title,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Schedule LinkedIn post failed'
    );
  }
};

export const generate_ai_campaign = async (
  business: Business,
  prompt: string,
  platforms: string[],
  connectedPlatforms: { mailchimp: boolean; linkedin: boolean }
) => {
  try {
    const response = await authedPost('marketing/generate_ai_campaign', {
      business,
      prompt,
      platforms,
      connectedPlatforms,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      'Generate AI campaign failed'
    );
  }
};

export const approve_ai_campaign = async (
  businessId: number,
  plan: Record<string, unknown>,
  selectedPlatforms: string[],
  scheduledSendTime?: string
) => {
  try {
    const response = await authedPost('marketing/approve_ai_campaign', {
      businessId,
      plan,
      selectedPlatforms,
      scheduledSendTime: scheduledSendTime || null,
    });
    return response.data as {
      campaignName: string;
      campaigns: Array<{ id: number; title: string; type: string; status: string }>;
    };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Approve AI campaign failed'
    );
  }
};

export type MarketingAnalyticsOverview = {
  publishedCampaigns: number;
  newsletter: {
    campaignsTracked: number;
    totals: { emailsSent: number; opens: number; clicks: number };
    avgOpenRate: number;
    avgCtr: number;
  };
  linkedin: {
    campaignsTracked: number;
    totals: { likes: number; comments: number };
  };
  audience: {
    memberCount: number;
    delta: number;
    listName?: string | null;
  } | null;
  recentPerformance: Array<{
    id: number;
    title: string;
    type: string;
    status: string;
    platformMeta?: {
      mailchimp?: {
        emailsSent?: number;
        uniqueOpens?: number;
        uniqueClicks?: number;
        openRate?: number;
        ctr?: number;
        lastSyncedAt?: string;
      };
      linkedin?: {
        likes?: number;
        comments?: number;
        analyticsAvailable?: boolean;
        lastSyncedAt?: string;
      };
    } | null;
    updatedAt: string;
  }>;
};

export const get_marketing_analytics = async (
  businessId: number
): Promise<MarketingAnalyticsOverview> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.get<MarketingAnalyticsOverview>('marketing/analytics', {
      params: { businessId },
      headers: { Authorization: `Bearer ${idToken}` },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Get marketing analytics failed'
    );
  }
};

export const sync_marketing_analytics = async (
  businessId: number
): Promise<{
  sync: {
    campaignsUpdated: number;
    audience?: { memberCount: number; delta: number } | null;
  };
  analytics: MarketingAnalyticsOverview;
}> => {
  try {
    const response = await authedPost('marketing/sync_analytics', { businessId });
    return response.data as {
      sync: {
        campaignsUpdated: number;
        audience?: { memberCount: number; delta: number } | null;
      };
      analytics: MarketingAnalyticsOverview;
    };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      'Sync marketing analytics failed'
    );
  }
};

export type SocialPlatform = 'instagram' | 'facebook' | 'x' | 'linkedin';

export type GeneratedSocialPost = {
  platform?: string;
  hook?: string;
  body?: string;
  hashtags?: string[];
  cta?: string;
  text?: string;
};

export const generate_social_post = async (
  business: Business,
  platform: SocialPlatform,
  prompt: string
): Promise<GeneratedSocialPost> => {
  try {
    const response = await authedPost('marketing/social/generate_post', {
      business,
      platform,
      prompt,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      'Generate social post failed'
    );
  }
};

export const save_social_draft = async (
  businessId: number,
  platform: Exclude<SocialPlatform, 'linkedin'>,
  text: string,
  title?: string
) => {
  try {
    const response = await authedPost('marketing/social/save_draft', {
      businessId,
      platform,
      text,
      title,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Save social draft failed'
    );
  }
};

export const get_marketing_platforms = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.get('marketing/platforms', {
      params: { businessId },
      headers: { Authorization: `Bearer ${idToken}` },
    });
    return response.data.platforms as Array<{
      id: string;
      label: string;
      publishMode: 'live' | 'draft_only';
      canPublish: boolean;
      connection: Record<string, unknown>;
    }>;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Get marketing platforms failed'
    );
  }
};